import React, { useEffect, useState } from 'react';
import { getCourse, getExpertByExternalId } from '@/services/strapiService';
import { StrapiImage } from '../Strapi/StrapiImage';
import type { Course } from '@/types/course';
import type { ExpertData } from '@/types/expert';
import { formatDate, formatDateTimeToBerlinTz, isLiveCourseActive } from '@/utils/helpers';
import Button from '../Button/Button';
import VerticalDotsButton from '../Icons/Action/VerticalDotsButton';
import Dropdown from '../Dropdown/Dropdown';
import { T, useTranslate } from '@tolgee/react';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@/config/routes';
import { useUser } from '@clerk/nextjs';
import Link from 'next/link';
import LoadingOverlay from '../Loader/LoadingOverlay';
import Image from 'next/image';
import { useBookingService } from '@/hooks/useBookingService';
import { useExpertService } from '@/hooks/useExpertService';
import { BookingTypes } from '@/types/booking';

type BookingCardProps = {
  bookings_id: string;
  bookings_type: BookingTypes;
  bookings_status: string;
  bookings_time: string;
  bookings_requested_at: string;
  bookings_created_at: string;
  bookings_updated_at: string;
  expert_id?: string;
  partner_id?: string;
  course_id?: string;
  booking_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  orientation_call_id?: string;
  past?: boolean;
  expertView?: boolean;
  expertExternalId?: string;
};

const BookingCard: React.FC<BookingCardProps> = ({
  bookings_id,
  bookings_type,
  bookings_time,
  bookings_status,
  expert_id,
  course_id,
  first_name,
  last_name,
  email,
  orientation_call_id,
  past = false,
  expertView = false,
  expertExternalId,
}) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { user } = useUser();
  const { approveBooking } = useBookingService();
  const { approveBooking: approveExpertBooking } = useExpertService();
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageAlt, setImageAlt] = useState<string>('');
  const [course, setCourse] = useState<Course | null>(null);
  const [expert, setExpert] = useState<ExpertData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isHappeningNow, setIsHappeningNow] = useState(false);

  const [pending, setPending] = useState(
    bookings_type === BookingTypes.THERAPIST_SESSION && bookings_status === 'CREATED' && expertView,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (
          (bookings_type === BookingTypes.THERAPIST_SESSION ||
            bookings_type === BookingTypes.ORIENTATION_CALL) &&
          expert_id
        ) {
          const { data: expert } = await getExpertByExternalId(expert_id || '');
          const expertData = expert[0];
          setImageSrc(
            expertData?.attributes?.picture.data?.attributes.formats.thumbnail?.url || '',
          );
          setImageAlt(`Expert: ${expertData.attributes.name}`);
          setExpert(expertData);
          setIsHappeningNow(
            new Date(bookings_time).getTime() - 30 * 60000 <= Date.now() &&
              new Date(bookings_time).getTime() + 30 * 60000 >= Date.now(),
          );
        } else if (bookings_type === BookingTypes.COURSE && course_id) {
          const { data: course } = await getCourse(course_id);
          setImageSrc(course.attributes.cover.data?.attributes.formats.thumbnail?.url || '');
          setImageAlt(`Course: ${course.attributes.title}`);
          setCourse(course);

          const active = isLiveCourseActive(course.attributes.time_range, course.attributes.date);

          setIsHappeningNow(active);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    void fetchData();
  }, [expert_id, course_id, orientation_call_id, bookings_type]);

  const renderTitle = () => {
    if (expertView) {
      if (bookings_type === BookingTypes.COURSE) {
        return course?.attributes.title ?? '';
      }

      if (first_name || last_name) {
        return `${first_name} ${last_name}`;
      } else if (email) {
        return email;
      } else {
        return 'Unknown User';
      }
    }
    if (bookings_type === BookingTypes.THERAPIST_SESSION) {
      return (
        <T
          keyName="booking_card.booked_expert.name"
          params={{ expert_name: expert?.attributes.name }}
          defaultValue={expert?.attributes.name}
        />
      );
    }
    if (bookings_type === BookingTypes.COURSE) {
      return course?.attributes.title;
    }
    if (bookings_type === BookingTypes.ORIENTATION_CALL) {
      return (
        <>
          <T
            keyName="booking_card.booked_expert.name"
            params={{
              expert_name: expert?.attributes.name,
            }}
            defaultValue={expert?.attributes.name}
          />
          <div className={`text-sm mt-1 ${isHappeningNow ? 'opacity-70' : 'opacity-40'}`}>
            {formatDateTimeToBerlinTz(bookings_time)}
          </div>
        </>
      );
    }
    return '';
  };

  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
  };

  const handleApproveBooking = async () => {
    setIsLoading(true);
    try {
      if (expertView && expertExternalId) {
        await approveExpertBooking(expertExternalId, bookings_id);
      } else {
        await approveBooking(bookings_id);
      }
      setPending(false);
    } catch (error) {
      console.error('Error approving booking:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMenuItems = (): {
    key: string;
    label: string;
    onClick?: () => void;
  }[] => {
    const menuItems: { key: string; label: string; onClick?: () => void }[] = [];

    if (expertView) {
      menuItems.push({
        key: '1',
        label: 'Message',
        onClick: () => {
          void router.push(ROUTES.EXPERT_CHAT(bookings_id));
          setIsLoading(true);
        },
      });

      return menuItems;
    }

    if (bookings_type !== BookingTypes.ORIENTATION_CALL) {
      menuItems.push({
        key: '1',
        label: t('booking_card.button.rebook.label', 'Nochmal buchen'),
        onClick: () => {
          if (bookings_type === BookingTypes.THERAPIST_SESSION) {
            void router.push(ROUTES.BOOK_EXPERT(expert_id as string));
          }
        },
      });
    }

    if (!past) {
      menuItems.push({
        key: '2',
        label: t('booking_card.popover.contact.label', 'Expert:in kontaktieren'),
        onClick: () => {
          void router.push(ROUTES.CHAT(bookings_id));
          setIsLoading(true);
        },
      });
    }

    return menuItems;
  };

  const shouldShowOverflowMenu = () => {
    return (
      (!isHappeningNow && bookings_type !== 'course') ||
      (past && bookings_type != BookingTypes.ORIENTATION_CALL)
    );
  };

  const resolveJoinNowLink = () => {
    if (!bookings_id) {
      console.log('Cant resolve meeting link, missing booking id');
      return;
    }

    if (expertView) {
      if (!expert_id) {
        console.log('Cant resolve meeting link, missing expert id');
        return;
      }
      return bookings_type === BookingTypes.COURSE
        ? ROUTES.EXPERT_LIVE_COURSE(expert_id, bookings_id)
        : ROUTES.EXPERT_MEETING(expert_id, bookings_id);
    }

    if (!user?.id) {
      console.log('Cant resolve meeting link, missing user info');
      return;
    }
    return bookings_type === BookingTypes.COURSE
      ? ROUTES.USER_LIVE_COURSE(bookings_id)
      : ROUTES.USER_EXPERT_SESSION_MEETING(user.id, bookings_id);
  };

  const menuItems = getMenuItems();

  return (
    <div
      className={`relative p-4 rounded-2xl flex items-center h-auto md:h-40 group transition-colors duration-300 
      ${pending ? 'bg-black' : isHappeningNow ? 'bg-primary' : 'bg-gray'}`}
    >
      <LoadingOverlay loading={isLoading} />

      <Link
        href={
          bookings_type === 'course' && !expertView
            ? ROUTES.BOOK_LIVE_COURSE(bookings_id)
            : bookings_type === 'course'
              ? ROUTES.EXPERT_BOOK_LIVE_COURSE(bookings_id)
              : bookings_type === 'therapist_session' && !expertView
                ? ROUTES.BOOKING_EXPERT_DETAILS(bookings_id)
                : bookings_type === 'therapist_session'
                  ? ROUTES.EXPERT_BOOKING_EXPERT_DETAILS(bookings_id)
                  : bookings_type === 'orientation_call' && !expertView
                    ? ROUTES.ORIENTATION_CALL_BOOKING(bookings_id)
                    : bookings_type === 'orientation_call'
                      ? ROUTES.EXPERT_ORIENTATION_CALL_BOOKING(bookings_id)
                      : ''
        }
        onClick={(_e) => {
          setIsLoading(true);
          /*if (bookings_type === 'orientation_call') {
            _e.preventDefault();
            router.push(ROUTES.HOME);
            dispatch(openOrientationCallModal());
          }*/
        }}
        className={`flex items-top md:items-center flex-grow`}
      >
        <div
          className={`relative overflow-hidden flex-shrink-0 ${
            bookings_type === 'course' || bookings_type === 'orientation_call'
              ? 'rounded-tl-[12px] rounded-tr-[12px] rounded-br-[50px] rounded-bl-[12px]'
              : 'rounded-full'
          }`}
          style={{ width: 120, height: 120 }}
        >
          {expertView ? (
            <Image
              alt={first_name + ' ' + last_name}
              className="object-cover h-full w-full"
              src="/img/booking/avatar.svg"
              height={120}
              width={120}
            />
          ) : (
            <StrapiImage
              alt={imageAlt}
              className="object-cover h-full w-full"
              src={imageSrc}
              height={120}
              width={120}
            />
          )}
          <StrapiImage
            alt={imageAlt}
            className="object-cover h-full w-full"
            src={imageSrc}
            height={120}
            width={120}
          />
        </div>

        <div className="ml-5 flex-grow flex flex-col gap-1 sm:gap-2">
          <div
            className={`text-xs uppercase  ${isHappeningNow || pending ? 'text-white opacity-70' : 'text-primary'}`}
          >
            {bookings_type === 'course' && <T>booking_card.booking_type.live_class.label</T>}
            {bookings_type === 'therapist_session' && (
              <T>booking_card.booking_type.session.label</T>
            )}
            {bookings_type === 'orientation_call' && (
              <T>booking_card.booking_type.orientation_call.label</T>
            )}
          </div>
          <div
            className={`text-base sm:text-base md:text-lg overflow-hidden ${
              bookings_type === 'therapist_session' ? 'line-clamp-2' : 'line-clamp-3'
            } ${(isHappeningNow || pending) && 'text-white'}`}
          >
            {renderTitle()}
          </div>
          <div
            className={`text-sm opacity-40 ${(isHappeningNow || pending) && 'text-white opacity-70'}`}
          >
            {bookings_type === 'therapist_session' && formatDateTimeToBerlinTz(bookings_time)}
            {bookings_type === 'course' && (
              <>
                {formatDate(bookings_time)} {course?.attributes.time_range}
              </>
            )}
          </div>
        </div>
      </Link>

      {shouldShowOverflowMenu() ? (
        <>
          <div
            className={`absolute top-3 right-3 cursor-pointer transition-all duration-300 ${
              dropdownOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            onClick={() => {
              handleDropdownToggle();
            }}
          >
            <VerticalDotsButton />
          </div>
          {/* Dropdown */}
          {dropdownOpen && (
            <div className="absolute top-10 right-3">
              <Dropdown
                menuItems={menuItems}
                isOpen={dropdownOpen}
                onToggle={handleDropdownToggle}
                onClose={handleDropdownClose}
                width="w-56"
                className="-mt-7"
              />
            </div>
          )}
        </>
      ) : (
        <div className="self-end relative">
          <div
            className={`transition-all duration-300 opacity-100 md:opacity-0 translate-y-0 md:translate-y-4 group-hover:opacity-100 group-hover:translate-y-0`}
          >
            {isHappeningNow ? (
              <Button href={resolveJoinNowLink()}>
                <T>booking_card.button.participate.label</T>
              </Button>
            ) : bookings_type === 'orientation_call' ? (
              <Button>
                <T>booking_card.button.participate.label</T>
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {bookings_type === 'therapist_session' && pending && (
        <div className="self-end absolute right-5">
          <div
            className={`transition-all duration-300 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0`}
          >
            <div className="mb-2">
              <Button
                onClick={() => {
                  void handleApproveBooking();
                }}
              >
                {t('expert_dashboard.booking_card.button.approve.label')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCard;
